import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CASTELLI } from "../constants";
import { Mail, Lock, User, MapPin, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { authAPI } from "../lib/api";

export default function Register() {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", castello: "" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password || !form.castello) {
      return toast.error("Compila tutti i campi");
    }
    if (!acceptedTerms) {
      return toast.error("Devi accettare Privacy Policy e Termini di Servizio");
    }
    setLoading(true);
    try {
      const { user, token } = await authAPI.register({ ...form, accepted_terms: true });
      loginWithToken(user, token);
      toast.success(`Benvenuto ${user.name} in SAN MARINO MARKET!`);
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore registrazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-black text-[#1d3d3a]">Crea il tuo account</h1>
          <p className="text-sm text-slate-500 mt-1">Riservato ai residenti della Repubblica di San Marino</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1.5">Nome completo</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Mario Rossi" className="pl-9 h-11"/>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
                <Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} type="email" placeholder="tu@email.com" className="pl-9 h-11"/>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
                <Input value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} type="password" placeholder="min. 6 caratteri" className="pl-9 h-11"/>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1.5">Castello di residenza</label>
              <Select value={form.castello} onValueChange={(v) => setForm({...form, castello: v})}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Seleziona il tuo castello"/></SelectTrigger>
                <SelectContent>
                  {CASTELLI.slice(1).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex items-start gap-2">
              <MapPin className="w-4 h-4 shrink-0 mt-0.5"/>
              <span>Solo residenti in Repubblica di San Marino possono vendere. La residenza verrà verificata alla prima vendita.</span>
            </div>

            <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer select-none" data-testid="terms-checkbox-row">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#e86a24]"
                data-testid="terms-checkbox"
              />
              <span>
                Ho letto e accetto la{" "}
                <Link to="/privacy" target="_blank" className="text-[#e86a24] font-semibold hover:underline">Privacy Policy</Link>{" "}
                e i{" "}
                <Link to="/termini" target="_blank" className="text-[#e86a24] font-semibold hover:underline">Termini di Servizio</Link>{" "}
                (GDPR — Reg. UE 2016/679).
              </span>
            </label>

            <Button type="submit" disabled={loading || !acceptedTerms} className="w-full h-11 bg-[#e86a24] hover:bg-[#c8561a] font-semibold disabled:opacity-60" data-testid="register-submit">
              <UserPlus className="w-4 h-4 mr-2"/>{loading ? "..." : "Crea account"}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-600 mt-5">
            Hai già un account? <Link to="/login" className="text-[#e86a24] font-semibold hover:underline">Accedi</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
